import _ from 'lodash'
import type { Unref } from '@wix/thunderbolt-catharsis'
import { Materializer, Update, Transform, createRef } from '@wix/materializer'
import { getDisplayedId, getFullId, getFullItemId, getUniqueId, REPEATER_DELIMITER } from '@wix/thunderbolt-commons'
import { BaseDataItem, Component, DataMapName, pointers, QueryProperty, ViewMode } from '@wix/thunderbolt-becky-types'
import type { RawModel } from '../../types'
import type {
	ConsumeRefParams,
	GetLeafsOverridesMapsRefs,
	GetOverrideDataRefs,
	GetOverrideTranslationDataRefs,
	PresetsRefsWithAncestors,
} from './types'
import {
	isRefComponent,
	getRefCreatorInNamespace,
	getLeafsOverridesMapAspectName,
	getContextAndTemplateCompIdFromInflatedCompId,
	hasRemoteOverride,
	hasSiteOverride,
} from '../byRefUtils'
import {
	DATA_MAP_NAMES,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	QUERY_MAPPING,
	REF_COMPONENTS_TEMPLATE_DATA_ASPECT,
	EMPTY_SCHEMA,
	RESOLVED_REF_COMP_PRESETS_ASPECT,
	STRUCTURE_ASPECT,
	COMP_ID_TO_INFLATED_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RESOLVED_TRANSLATIONS_ASPECT,
	ROOT_COMP_ID_ASPECT,
	DATA_QUERY_TO_COMP_ID_ASPECT,
	INVALIDATIONS_NAMESPACE,
	INFLATED_MAP_TO_DATA_MAP_NAME,
	RESOLVED_REF_COMP,
	VARIANTS_TYPES_TO_INFLATE,
	SHARED_PARTS_COMP_ID_TO_ROOT_ID,
} from '../constants'
import { defaultRefConsumptionDataNodeToViewItem, getDataItemRefsWithOverrides } from './dataConsumption'
import { inflateCompStructure } from './inflateCompStructure'
import { getPresetsRefsWithAncestors, getRefCompResolvedPresetsRef } from './presetsResolving'
import { consumptionCompNodes, consumptionDataNodes } from './consumptionNodes'
import { sharedPartWidgetId } from '../../utils'

const RETURN_EMPTY_ARRAY_FN = () => []

const queryNames = Object.keys(QUERY_MAPPING) as Array<QueryProperty>

const {
	data: {
		DATA_MAPS: { SLOTS, VARIANTS },
	},
	components: {
		PROPERTY_TYPES: { DATA_QUERY, DESIGN_QUERY },
	},
	QUERY_TO_MAP_NAME,
} = pointers

export const getRefConsumptionTransformer = (rawModel: RawModel): Transform => (value, path) => {
	if (!value) {
		return value
	}

	const [, aspect, id] = path

	if (aspect === RESOLVED_REF_COMP_PRESETS_ASPECT) {
		const { ownPresets, parentRefCompResolvedPresets } = value as Unref<PresetsRefsWithAncestors>
		const ownPresetsValues = ownPresets
			.flatMap((v) => (v?.type === 'RefArray' ? v.values : v ? [v] : []))
			.filter((v) => v)
		return [ownPresetsValues, ...(parentRefCompResolvedPresets || [])]
	}

	if (aspect in consumptionCompNodes) {
		const compStructure = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, id])
		return consumptionCompNodes[aspect].toViewItem(compStructure, value)
	}

	const fixedAspect = INFLATED_MAP_TO_DATA_MAP_NAME[aspect] || aspect
	if (DATA_MAP_NAMES.has(fixedAspect as DataMapName)) {
		if (consumptionDataNodes[fixedAspect as DataMapName]?.toViewItem) {
			return consumptionDataNodes[fixedAspect as DataMapName]?.toViewItem!({ id, aspect, value, rawModel })
		}

		return defaultRefConsumptionDataNodeToViewItem(id, value)
	}

	return value
}

export const createBatchedRefConsumer = (materializer: Materializer, rawModel: RawModel, update: Update<void>) => {
	const viewMode = materializer.get<ViewMode>([ENVIRONMENT_NAMESPACE, ENVIRONMENT_NAMESPACE, 'viewMode'])
	const isMobileView = viewMode === 'mobile'

	const inflatedRepeatedDataItemIds: Record<string, true> = {}

	const inflateWidgetTemplate = ({
		pageId,
		templateKey,
		rootCompId,
		ancestorsChain,
		getOverrideDataRefs,
		getOverrideTranslationDataRefs,
		getLeafsOverridesMapsRefs,
		isSharedPart,
	}: {
		pageId: string
		templateKey: string
		rootCompId: string
		ancestorsChain: string
		getOverrideDataRefs: GetOverrideDataRefs
		getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs
		getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs
		isSharedPart: boolean
	}) => {
		const createRefInTemplateNamespace = getRefCreatorInNamespace(templateKey)
		const getParentRefCompResolvedPresets = () => getRefCompResolvedPresetsRef(ancestorsChain)

		const inflateDataItem = (
			inflatedDataItemId: string,
			queryName: QueryProperty,
			templateComponent: Component,
			inflatedComponent: Component,
			itemSuffix = ''
		) => {
			const dataMapName = QUERY_MAPPING[queryName]

			const getDependenciesData = {
				rawModel,
				isMobileView,
				queryName,
				widgetNamespace: templateKey,
				templateComponent,
				getOverrideDataRefs,
				createRefInTemplateNamespace,
				getOverrideTranslationDataRefs,
				getLeafsOverridesMapsRefs,
				getParentRefCompResolvedPresets,
				itemSuffix,
				inflatedDataItemId,
			}

			const dependencies = consumptionDataNodes[dataMapName]?.getDependencies?.(getDependenciesData) || {
				dataItemWithOverrides: getDataItemRefsWithOverrides(getDependenciesData),
			}

			if (consumptionDataNodes[dataMapName]?.updateDependencies) {
				consumptionDataNodes[dataMapName]!.updateDependencies!({
					update,
					inflatedComponent,
					dependencies,
					id: inflatedDataItemId,
				})
			} else {
				update(RESOLVED_REFS_NAMESPACE, dataMapName, inflatedDataItemId, dependencies, dependencies)
			}
		}

		const inflateRepeatedDataItems = () => {
			;[DATA_QUERY, DESIGN_QUERY].forEach((queryName) => {
				const data = _.get(rawModel, [templateKey, QUERY_TO_MAP_NAME[queryName]])
				_.forEach(data, (__, dataItemId) => {
					const repeatedItemScope = getFullItemId(dataItemId) // dataItem-ksoawjjj__itemInner__itemOuter => itemInner__itemOuter
					if (!repeatedItemScope) {
						return
					}

					const repeatedItemTemplateId = getFullId(dataItemId) // comp-ksoaxbli_r_comp-ksekvwbd-dataQuery or dataItem-ksoawjjj
					const compId = repeatedItemTemplateId.endsWith(queryName)
						? repeatedItemTemplateId.split(`-${queryName}`)[0]
						: _.get(rawModel, [templateKey, DATA_QUERY_TO_COMP_ID_ASPECT, repeatedItemTemplateId])

					const inflatedCompId = getUniqueId(ancestorsChain, compId)
					const inflatedComp = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedCompId])
					if (!inflatedComp) {
						return
					}
					const inflatedDataItemId = getDisplayedId(inflatedComp[queryName], repeatedItemScope)

					if (inflatedRepeatedDataItemIds[inflatedDataItemId]) {
						// if the component data under a certain repeater item suffix is already inflated, return.
						// if we wouldn't return, an outer override would override the inner overrides.
						return
					}

					// horrible hack. since we may stumble upon a data override of a component in the context of inflating an outer widget,
					// we need to create a "templateComp" which is actually inflated up to the outer widget level.
					const templateComp = { id: compId, [queryName]: repeatedItemTemplateId } as Component

					inflateDataItem(
						inflatedDataItemId,
						queryName,
						templateComp,
						inflatedComp,
						`${REPEATER_DELIMITER}${repeatedItemScope}`
					)

					inflatedRepeatedDataItemIds[inflatedDataItemId] = true
				})
			})
		}

		const inflateSlotComponentsOverrides = (innerRefCompId: string) => {
			const slotsData = _.get(rawModel, [templateKey, SLOTS], {})
			_(slotsData)
				.pickBy((slotsDataItem, slotsDataItemId) => slotsDataItem && slotsDataItemId.startsWith(innerRefCompId))
				.flatMap((slotsDataItem) => _.values(slotsDataItem.slots))
				.forEach((id) => inflateTemplateStructure(id))
		}

		const inflateVariantsComponents = () => {
			const variants = _.get(rawModel, [templateKey, VARIANTS])
			_(variants)
				.pickBy((variantDataItem) => variantDataItem) // filter out variants dataItems that where deleted (value is undefined)
				.forEach((variant) => {
					if (VARIANTS_TYPES_TO_INFLATE.includes(variant.type)) {
						// maybe here we need to also use full id?
						const inflatedDataItemId = getUniqueId(ancestorsChain, variant.id)
						const deps = createRefInTemplateNamespace<BaseDataItem>(VARIANTS, variant.id)
						update(RESOLVED_REFS_NAMESPACE, VARIANTS, inflatedDataItemId, deps, deps)
					}
				})
		}

		const inflateTemplateStructure = (compId: string, isRootComp: boolean = false): void => {
			const templateComp = _.get(rawModel, [templateKey, STRUCTURE_ASPECT, compId])

			if (!templateComp) {
				return
			}

			if (Array.isArray(templateComp.components)) {
				for (const childId of templateComp.components) {
					inflateTemplateStructure(childId)
				}
			}

			const inflatedComp = inflateCompStructure({
				rawModel,
				pageId,
				templateComp,
				ancestorsChain,
				isRootComp,
				viewMode,
			})

			if (isRefComponent(inflatedComp)) {
				const refCompChildId = consumeRef({
					refCompId: compId,
					refCompAncestorTemplateKey: templateKey,
					refCompAncestorsChain: ancestorsChain,
					pageId,
					getAncestorsOverrideDataRefs: getOverrideDataRefs,
					getAncestorsOverrideTranslationDataRefs: getOverrideTranslationDataRefs,
					getAncestorsLeafsOverridesMapsRefs: getLeafsOverridesMapsRefs,
				})

				// refCompChildId can be undefined if its refComp template wasn't loaded yet
				inflatedComp.components = refCompChildId ? [refCompChildId] : []

				inflateSlotComponentsOverrides(compId)
			}

			update(RESOLVED_REFS_NAMESPACE, COMP_ID_TO_INFLATED_COMP_ID_ASPECT, compId, inflatedComp.id, EMPTY_SCHEMA)
			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedComp.id], inflatedComp)
			if (isSharedPart) {
				_.set(rawModel, [SHARED_PARTS_COMP_ID_TO_ROOT_ID, compId], rootCompId)
			}

			for (const compNodeAspect in consumptionCompNodes) {
				const dependencies = consumptionCompNodes[compNodeAspect].getDependencies(
					compId,
					inflatedComp,
					ancestorsChain,
					createRefInTemplateNamespace,
					isMobileView
				)
				update(RESOLVED_REFS_NAMESPACE, compNodeAspect, inflatedComp.id, dependencies, dependencies)
			}

			_.forEach(queryNames, (queryName) => {
				const compHasSiteOverride = hasSiteOverride(rawModel, QUERY_MAPPING[queryName], inflatedComp.id)
				const compHasRemoteOverride = hasRemoteOverride(rawModel, queryName, inflatedComp.id)
				const inflatedQuery = inflatedComp[queryName]
				if (
					!inflatedQuery ||
					(templateComp[queryName] && templateComp[queryName] === inflatedQuery) ||
					(!templateComp[queryName] && !compHasSiteOverride && !compHasRemoteOverride)
				) {
					return
				}
				inflateDataItem(inflatedQuery, queryName, templateComp, inflatedComp)
			})

			// we remove slots from the children array in flattenStructure.ts, which is invoked both on site assets and remote widget jsons.
			// so we need to inflate those orphan comps here
			const slots = _.get(rawModel, [templateKey, SLOTS, templateComp.slotsQuery!, 'slots'])
			_(slots)
				.values()
				.forEach((id) => inflateTemplateStructure(id))
		}

		// 1. DFS all the structure of {templateKey}, inflate all comps in the context of {ancestorsChain}
		inflateTemplateStructure(rootCompId, true)
		// 2. Inflate all orphan (not pointed by structure) components and data items
		inflateRepeatedDataItems()
		inflateVariantsComponents()
	}

	const consumeRef = ({
		refCompId,
		refCompAncestorTemplateKey,
		refCompAncestorsChain,
		pageId,
		getAncestorsOverrideDataRefs,
		getAncestorsOverrideTranslationDataRefs,
		getAncestorsLeafsOverridesMapsRefs,
	}: ConsumeRefParams) => {
		const innerWidgetTemplateData = materializer.get([
			refCompAncestorTemplateKey,
			REF_COMPONENTS_TEMPLATE_DATA_ASPECT,
			refCompId,
		])

		// refComp deletion - the dataQuery item is updated with undefined so refComponentsTemplateData is recalculated and its value will be undefined as well
		if (!innerWidgetTemplateData) {
			return
		}

		const {
			templateKey: innerWidgetTemplateKey,
			isSharedPart,
			rootCompId,
			pageId: innerWidgetPageId,
		} = innerWidgetTemplateData

		const inflatedRefCompId = refCompAncestorsChain ? getUniqueId(refCompAncestorsChain, refCompId) : refCompId

		const prevTemplateDependency = materializer.get([
			refCompAncestorTemplateKey,
			INVALIDATIONS_NAMESPACE,
			inflatedRefCompId,
		])

		const templateDependencyId = isSharedPart
			? sharedPartWidgetId(innerWidgetPageId, rootCompId)
			: innerWidgetPageId || innerWidgetTemplateKey

		// the refComp is new or the templateKey was changed (can happen between mobile and desktop in classic sites)
		if (!prevTemplateDependency || prevTemplateDependency.id !== templateDependencyId) {
			// refComp depends on templateDependencyId, if the page or templateKey is invalidated, we want to reconsume the refComp
			const templateRef = createRef([INVALIDATIONS_NAMESPACE, INVALIDATIONS_NAMESPACE, templateDependencyId])
			update(
				refCompAncestorTemplateKey,
				INVALIDATIONS_NAMESPACE,
				inflatedRefCompId,
				{ templateRef, id: templateDependencyId },
				{ templateRef }
			)
		}

		const innerWidgetRootCompId = rootCompId
			? rootCompId
			: _.get(rawModel, [innerWidgetTemplateKey, ROOT_COMP_ID_ASPECT])

		// refComp template wasn't loaded yet. no need to inflate it until it's loaded
		if (!innerWidgetRootCompId) {
			return
		}

		const refCompChildId = getUniqueId(inflatedRefCompId, innerWidgetRootCompId)
		_.set(rawModel, [RESOLVED_REFS_NAMESPACE, inflatedRefCompId], refCompChildId)

		const createRefInRefCompNamespace = getRefCreatorInNamespace(refCompAncestorTemplateKey)

		const getOverrideDataRefs: GetOverrideDataRefs = ({
			compId,
			queryName,
			itemSuffix = '',
			templateDataId = '',
		}) => {
			const dataMapName = QUERY_MAPPING[queryName]
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideDataRefs({ compId: inflatedCompId, queryName, templateDataId, itemSuffix }),
				createRefInRefCompNamespace(
					dataMapName,
					`${inflatedCompId}-${templateDataId ? templateDataId : queryName}${itemSuffix}`
				),
			]
		}

		const getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs = (compId: string, itemSuffix = '') => {
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideTranslationDataRefs(inflatedCompId, itemSuffix),
				createRefInRefCompNamespace(RESOLVED_TRANSLATIONS_ASPECT, `${inflatedCompId}-dataQuery${itemSuffix}`),
			]
		}

		const getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs = (inflatedCompIdInNamespace, queryName) => {
			/**
			 * there are 2 options for `inflatedCompIdInNamespace`:
			 * 1. 	If it's the first one in the recursion, it is the template comp id.
			 * 		In this case, the context is the parentRefCompId
			 * 2.	If it's further into the recursion, it is a partially inflated comp id.
			 * 		Meaning that it is inflated up to this point (e.g. outerRefCompId_r_templateCompId).
			 * 		In this case, we first inflate it to the current level (e.g. parentRefCompId_r_outerRefCompId_r_templateCompId),
			 * 		then extract the context (parentRefCompId_r_outerRefCompId) and the templateCompId
			 */
			const inflatedCompId = getUniqueId(refCompId, inflatedCompIdInNamespace)
			const { context, templateCompId } = getContextAndTemplateCompIdFromInflatedCompId(inflatedCompId)
			return [
				...getAncestorsLeafsOverridesMapsRefs(inflatedCompId, queryName),
				createRefInRefCompNamespace(getLeafsOverridesMapAspectName(context, queryName), templateCompId),
			]
		}

		inflateWidgetTemplate({
			pageId,
			templateKey: innerWidgetTemplateKey,
			rootCompId: innerWidgetRootCompId,
			ancestorsChain: inflatedRefCompId,
			getOverrideDataRefs,
			getOverrideTranslationDataRefs,
			getLeafsOverridesMapsRefs,
			isSharedPart,
		})

		const refComp = _.get(rawModel, [refCompAncestorTemplateKey, STRUCTURE_ASPECT, refCompId])
		if (refComp.presetsQuery) {
			const presetsRefsWithAncestors = getPresetsRefsWithAncestors(
				refComp,
				refCompAncestorsChain,
				createRefInRefCompNamespace,
				getAncestorsOverrideDataRefs
			)
			update(
				RESOLVED_REFS_NAMESPACE,
				RESOLVED_REF_COMP_PRESETS_ASPECT,
				inflatedRefCompId,
				presetsRefsWithAncestors,
				presetsRefsWithAncestors
			)
		}

		if (!refCompAncestorsChain) {
			// the refComp is in the page json so its components array hasn't been populated yet
			const resolvedRefComp =
				isSharedPart && !_.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, innerWidgetRootCompId])
					? {
							...refComp,
							componentType: 'DivWithChildren',
							components: [],
					  }
					: {
							...refComp,
							components: [refCompChildId],
					  }

			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, refCompId], resolvedRefComp)
			update(RESOLVED_REFS_NAMESPACE, RESOLVED_REF_COMP, refCompId, resolvedRefComp, EMPTY_SCHEMA)
		}

		return refCompChildId
	}

	return ({ refCompId }: Pick<ConsumeRefParams, 'refCompId'>) =>
		consumeRef({
			pageId: _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, refCompId, 'metaData', 'pageId']),
			refCompId,
			refCompAncestorTemplateKey: SITE_NAMESPACE,
			refCompAncestorsChain: '',
			getAncestorsOverrideDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsOverrideTranslationDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsLeafsOverridesMapsRefs: RETURN_EMPTY_ARRAY_FN,
		})
}
